// Pull down at the top of a screen to ask it again (B-312, DIRECTION U20).
//
// Material's swipe to refresh, which iOS apps share: at the top of a list,
// pull down and a round indicator follows the finger; let go past the mark
// and it spins until the screen's reads have answered, then goes. Both
// platforms say content should update by itself and this is reassurance on
// top (the scores already ask again on their own, B-263), so nothing here is
// reachable only by pulling. It asks again — it never reloads the page, which
// is what Chrome's own pull did to an installed app, and which the frame
// turns off (`overscroll-behavior` on `html`).
//
// Touch only, and only where nothing under the finger is scrolled down: on
// any screen, whatever scrolls it (the screen in flow, a dashboard's widgets,
// a form's box). A pull that starts in a sideways list or a field is theirs. Moves the indicator by
// hand, frame by frame, so it lives in the kit; what "ask again" means is the
// caller's.

import { useEffect, useRef, useState, type RefObject } from 'react';
import { Icon } from './Icon';

/** How far the finger travels for the indicator to arm (px). */
const ARM = 72;
/** The indicator moves half as far as the finger, and no further than this. */
const MAX = 96;
/** Where it rests while the screen is asked again. */
const REST = 56;
const SLOP = 8;

/** Is anything between `from` and `area` scrolled down, so a pull scrolls it instead? */
function scrolledDown(from: Element | null, area: HTMLElement): boolean {
  for (let el = from; el !== null && el !== area.parentElement; el = el.parentElement) {
    if (!(el instanceof HTMLElement)) continue;
    const style = getComputedStyle(el);
    if (/(auto|scroll)/.test(style.overflowY) && el.scrollTop > 0) return true;
    if (el === area) break;
  }
  return false;
}

export function PullToRefresh({
  area,
  onRefresh,
}: {
  /** What a pull starts in: the screen's content. */
  area: RefObject<HTMLElement | null>;
  /** Ask again; settles when every read has answered. */
  onRefresh: () => Promise<void>;
}) {
  const mark = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState('');
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;

  useEffect(() => {
    const el = area.current;
    const ind = mark.current;
    if (el === null || ind === null) return;
    let startY = 0;
    let startX = 0;
    let pulling = false;
    let refused = true;
    let travel = 0;
    let running = false;

    const place = (y: number, turn: number, animate: boolean) => {
      ind.style.transition = animate
        ? 'transform 200ms cubic-bezier(0.2, 0, 0, 1), opacity 200ms'
        : 'none';
      ind.style.transform = `translate(-50%, ${y - REST}px) rotate(${turn}deg)`;
      ind.style.opacity = y > 4 ? '1' : '0';
    };

    const onStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      const target = event.target instanceof Element ? event.target : null;
      refused =
        running ||
        event.touches.length !== 1 ||
        touch === undefined ||
        scrolledDown(target, el) ||
        target?.closest(
          'input, textarea, select, [contenteditable="true"], .fsp-table-scroll[data-sideways]',
        ) != null;
      pulling = false;
      if (touch !== undefined) {
        startY = touch.clientY;
        startX = touch.clientX;
      }
    };

    const onMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (refused || touch === undefined) return;
      const dy = touch.clientY - startY;
      const dx = touch.clientX - startX;
      if (!pulling) {
        if (Math.abs(dx) > SLOP && Math.abs(dx) > Math.abs(dy)) {
          refused = true;
          return;
        }
        if (dy < -SLOP) {
          refused = true;
          return;
        }
        if (dy <= SLOP) return;
        pulling = true;
      }
      event.preventDefault();
      travel = Math.max(0, dy - SLOP);
      const y = Math.min(MAX, travel / 2);
      ind.dataset.armed = travel >= ARM ? 'true' : '';
      place(y, (travel / ARM) * 270, false);
    };

    const onEnd = () => {
      if (!pulling) return;
      pulling = false;
      if (travel < ARM) {
        place(0, 0, true);
        return;
      }
      running = true;
      setBusy(true);
      setSaid('');
      place(REST, 0, true);
      // A spin too short to see reads as nothing having happened.
      const least = new Promise((resolve) => window.setTimeout(resolve, 450));
      void Promise.all([refresh.current().catch(() => undefined), least]).then(() => {
        running = false;
        setBusy(false);
        setSaid('Updated');
        ind.dataset.armed = '';
        place(0, 0, true);
      });
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    place(0, 0, false);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [area]);

  return (
    <>
      <div ref={mark} className="fsp-ptr" data-busy={busy || undefined} aria-hidden>
        <Icon name="refresh" />
      </div>
      {/* Said once it is done, for a screen reader; nothing to see. */}
      <span className="fsp-sr" role="status">
        {said}
      </span>
    </>
  );
}
